import { useEffect } from 'react';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { requestProHardwareBack } from './proBackPolicy';

/**
 * Wire Android hardware back into the Pro leave policy (`requestProHardwareBack`).
 *
 * Policy when the listener fires:
 * - Open `.pro-sheet` → dismiss overlay
 * - Registered leave handler (dirty form, drawer, …) → handler decides
 * - Else if web history can go back → `window.history.back()`
 * - Else at stack root → `App.exitApp()` (device QA residual: confirm exit vs home)
 *
 * No-op on web and iOS (no hardware back button). Mount once at app root
 * alongside `useProSwipeBack`.
 */
export function useProHardwareBack(): void {
  useEffect(() => {
    if (Capacitor.getPlatform() !== 'android') return;
    if (!Capacitor.isNativePlatform()) return;

    let cancelled = false;
    const handlePromise = App.addListener(
      'backButton',
      ({ canGoBack }: { canGoBack: boolean }) => {
        requestProHardwareBack(() => {
          if (canGoBack) {
            window.history.back();
            return;
          }
          App.exitApp();
        });
      },
    );

    return () => {
      cancelled = true;
      handlePromise
        .then((handle) => {
          if (cancelled) return handle.remove();
        })
        .catch(() => undefined);
    };
  }, []);
}
